<i18n>
{
  "en": {
    "menu": "Menu to filter",
    "section": "Section to filter",
    "saleZone": "Sale zones to filter",
    "saveChanges": "Save changes",
    "applyFilters": "Apply filters",
    "dates": "Filter dates",
    "loadError": "Error loading data of sale rates and menus",
    "changeFiltersTitle": "Change search parameters",
    "changeFiltersDescription": "Unsaved changes will be discarded.",
    "priceChangeTooltip": "Export price change"
  },
  "es": {
    "menu": "Menu a filtrar",
    "section": "Sección a filtrar",
    "saleZone": "Zonas a filtrar",
    "saveChanges": "Guardar cambios",
    "applyFilters": "Aplicar filtros",
    "dates": "Fechas a filtrar",
    "loadError": "Error al cargar los datos de tarifas y menús",
    "changeFiltersTitle": "Cambio de parámetros de búsqueda",
    "changeFiltersDescription": "Se descartarán los cambios no guardados.",
    "priceChangeTooltip":  "Exportar cambio de precios"
  }
}
</i18n>

<template>
  <div v-if="!areFiltersLoading" class="d-flex flex-row flex-wrap justify-center align-center">
    <date-picker ref="datepicker" v-model="dateRange" is-range trim-weeks
                 :min-date="minDate" :max-date="maxDate" :locale="userLocale"
                 @input="onDatePicker" class="d-flex align-center mr-10 data-picker-size">
      <template v-slot="{ togglePopover }">
        <v-btn @click="togglePopover({ placement: 'auto-start' })" rounded elevation="0" outlined>
          <v-icon class="mr-3">mdi-calendar</v-icon>
          <span>{{ dateToDisplay }}</span>
        </v-btn>
      </template>
    </date-picker>

    <v-select v-model="saleRate" :items="saleZonesToDisplay" item-text="saleZones"
              item-value="saleRate" :label="this.$t('saleZone')" prepend-icon="mdi-view-quilt-outline"
              @change="onSaleRateSelected" class="mr-10 select-size"/>
    <v-select v-model="menuGuid" :disabled="menuFilterIsDisabled" :items="menuFilters"
              item-text="menuTitle" item-value="menuGuid" :label="this.$t('menu')"
              @change="onMenuSelected" prepend-icon="mdi-silverware-variant" class="mr-10 select-size"/>
    <v-select v-model="sectionGuid" :disabled="sectionFilterIsDisabled" :items="sectionsFilters"
              item-text="sectionTitle" item-value="sectionGuid" :label="this.$t('section')"
              @change="onSectionSelected" prepend-icon="mdi-book-open-outline" class="mr-10 select-size"/>

    <v-tooltip top>
      <template v-slot:activator="{ on, attrs }">
        <div class="reset-button d-inline-flex flex-grow-0 mr-3" v-on="on" v-bind="attrs">
          <v-btn icon @click="onApplyFilters" :disabled="isDisabledApplyFilters">
            <v-icon color="grey" large class="mb-2">mdi-cloud-search</v-icon>
          </v-btn>
        </div>
      </template>
      <span>{{ this.$t('applyFilters') }}</span>
    </v-tooltip>
    <v-tooltip top>
      <template v-slot:activator="{ on, attrs }">
        <div class="reset-button d-inline-flex flex-grow-0 mr-3" v-on="on" v-bind="attrs">
          <v-btn icon @click="onSave" :disabled="!hasChanges">
              <v-icon color="grey" large class="mb-2">mdi-content-save</v-icon>
          </v-btn>
        </div>
      </template>
      <span>{{ this.$t('saveChanges') }}</span>
    </v-tooltip>
    <v-tooltip top>
      <template v-slot:activator="{ on, attrs }">
        <div class="reset-button d-inline-flex flex-grow-0" v-on="on" v-bind="attrs">
          <v-btn icon @click="onExportPriceChanges" :disabled="isDisabledExportPriceChanges">
              <v-icon color="grey" large class="mb-2">mdi-file-export</v-icon>
          </v-btn>
        </div>
      </template>
      <span>{{ this.$t('priceChangeTooltip') }}</span>
    </v-tooltip>
    <dynameat-confirmation-modal :visible="changeFiltersModalVisible" :title="$t('changeFiltersTitle')"
                                 :description="$t('changeFiltersDescription')" @confirm="onConfirmChangeFilters"
                                 @cancel="onCloseChangeFiltersModal"/>
  </div>
  <v-skeleton-loader v-else type="chip@4, avatar@3" elevation="2"
                     class="d-flex flex-row align-center justify-space-around mt-4 pa-3 align-self-center skeleton-size"/>
</template>

<script>

import Vue from 'vue';
import Component from 'vue-class-component';
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import {
  Components, MapActions, MapGetters
} from '@core/helpers/decorators';
import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import { Prop, Watch } from 'vue-property-decorator';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';

Component.registerHooks(['beforeRouteLeave']);
@Component
@Components({ DatePicker, DynameatConfirmationModal })
@MapActions(['saleRateListRequest', 'menuListRequest', 'menuFilterListRequest', 'addNotification',
  'setDateRangeFilter', 'setMenuGuidFilter', 'setSectionGuidFilter', 'setSaleRateFilter'
])
@MapGetters(['saleRateList', 'menuList', 'menuFilterList', 'predictionPeriodList', 'userLocale',
  'menuGuidFilter', 'sectionGuidFilter', 'saleRateFilter', 'dateRangeFilter'])
export default class PriceManagerFilters extends Vue {
  @Prop({ type: Boolean, default: false }) hasChanges;

  menuGuid = null

  sectionGuid = ''

  saleRate = null

  dateRange = null

  oldMenuGuidFilter = null

  oldSectionGuidFilter = null

  oldSaleRateGuidFilter = null

  oldStartDateFilter = null

  oldEndDateFilter = null

  changeFiltersModalVisible = false

  areFiltersLoading = false

  async created() {
    try {
      this.areFiltersLoading = true;
      await this.saleRateListRequest(this.$route.params.pointOfSaleGuid);
      await this.menuListRequest(this.$route.params.pointOfSaleGuid);
      await this.menuFilterListRequest(this.$route.params.pointOfSaleGuid);
      this.syncFilters();
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('loadError')));
    } finally {
      this.areFiltersLoading = false;
    }
  }

  syncFilters() {
    let dateRangeTarget = null;
    if (this.dateRangeFilter) {
      dateRangeTarget = {
        start: new Date(this.dateRangeFilter.start.getTime()),
        end: new Date(this.dateRangeFilter.end.getTime())
      };
    }
    this.dateRange = dateRangeTarget;
    this.saleRate = this.saleRateFilter;
    this.menuGuid = (this.menuGuidFilter) ? this.menuGuidFilter : null;
    if (this.menuGuid && this.sectionGuidFilter) {
      const menuSelected = this.menuFilterList.find((menu) => menu.menuGuid === this.menuGuidFilter);
      const sectionSelected = menuSelected.sections.find((section) => section.sectionGuid === this.sectionGuidFilter);
      this.sectionGuid = (sectionSelected && !sectionSelected.isDisabled(true)) ? this.sectionGuidFilter : '';
    }
  }

  getCurrentDatePlusDays = (days) => {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + days);
    return currentDate;
  };

  get flatDatesList() {
    return this.predictionPeriodList?.flatMap((period) => [period.startDate, period.endDate]);
  }

  get minDate() {
    const fiveDaysAgo = this.getCurrentDatePlusDays(-5);
    const recentPeriod = this.predictionPeriodList.find((period) => period.startDate <= fiveDaysAgo && period.endDate >= fiveDaysAgo);
    // If we find a recent period that includes prices five days ago, use that date
    if (recentPeriod) {
      return fiveDaysAgo;
    }
    // If we can't, use the start date of the next period generated
    const recentDatesList = this.flatDatesList.filter((date) => date > fiveDaysAgo);
    return recentDatesList?.reduce((prev, curr) => (prev && prev < curr ? prev : curr), null);
  }

  get maxDate() {
    return this.flatDatesList?.reduce((prev, curr) => (prev && prev > curr ? prev : curr), null);
  }

  get saleZonesToDisplay() {
    const zones = this.saleRateList.map((saleRate) => ({ saleRate, saleZones: saleRate.saleZoneNames }));
    return zones.filter((zone) => !!zone.saleZones);
  }

  get menuFilterIsDisabled() {
    return !this.saleRateFilter;
  }

  get menuFilters() {
    let menusToShow = [];
    if (this.saleRateFilter) {
      // eslint-disable-next-line camelcase
      const saleZoneGuids = this.saleRateFilter.sale_zones.map((sale_zone) => sale_zone.guid);
      // eslint-disable-next-line camelcase
      const menus = this.menuList.filter((menu) => menu.sale_zones.some((sale_zone_guid) => saleZoneGuids.includes(sale_zone_guid)));
      const menuGuids = menus.map((menu) => menu.guid);
      menusToShow = this.menuFilterList.filter((menu) => menuGuids.includes(menu.menuGuid));
    }

    menusToShow.unshift('');
    return menusToShow;
  }

  get sectionFilterIsDisabled() {
    return !this.menuGuidFilter;
  }

  get sectionsFilters() {
    const sectionsToShow = [];
    if (this.menuGuidFilter) {
      const menuFilter = this.menuFilters.find((filter) => filter.menuGuid === this.menuGuidFilter);
      if (menuFilter) {
        sectionsToShow.push(...menuFilter.getOrderedSections(true));
        sectionsToShow.unshift('');
      }
    }
    return sectionsToShow;
  }

  get dateToDisplay() {
    return (this.dateRangeFilter) ? this.selectedDateFormat : this.$t('dates');
  }

  get selectedDateFormat() {
    if (this.dateRangeFilter.start.getTime() === this.dateRangeFilter.end.getTime()) {
      return `${this.dateRangeFilter.start.toLocaleDateString()}`;
    }
    return `${this.dateRangeFilter.start.toLocaleDateString()} - ${this.dateRangeFilter.end.toLocaleDateString()}`;
  }

  get isDisabledApplyFilters() {
    return !this.dateRangeFilter || !this.saleRateFilter || !this.filtersHaveChanged;
  }

  get filtersHaveChanged() {
    return this.oldMenuGuidFilter !== this.menuGuidFilter || this.oldSectionGuidFilter !== this.sectionGuidFilter
      || this.oldSaleRateGuidFilter !== this.saleRateFilter.guid
      || this.oldStartDateFilter?.toISOString() !== this.dateRangeFilter?.start.toISOString()
      || this.oldEndDateFilter?.toISOString() !== this.dateRangeFilter?.end.toISOString();
  }

  get isDisabledExportPriceChanges() {
    return !this.dateRangeFilter;
  }

  onApplyFilters() {
    if (this.hasChanges) {
      this.changeFiltersModalVisible = true;
    } else {
      this.emitFilters();
    }
  }

  emitFilters() {
    const filters = {
      dates: {
        startDate: this.getFormatDate(this.dateRangeFilter.start),
        endDate: this.getFormatDate(this.dateRangeFilter.end)
      },
      menu: this.menuGuidFilter,
      section: this.sectionGuidFilter,
      saleRate: this.saleRateFilter.guid
    };

    this.oldMenuGuidFilter = this.menuGuidFilter;
    this.oldSectionGuidFilter = this.sectionGuidFilter;
    this.oldSaleRateGuidFilter = this.saleRateFilter.guid;
    this.oldStartDateFilter = new Date(this.dateRangeFilter.start.getTime());
    this.oldEndDateFilter = new Date(this.dateRangeFilter.end.getTime());

    this.$emit('apply-filters', filters);
  }

  onSave() {
    this.$emit('save');
  }

  onExportPriceChanges() {
    const startDate = this.getFormatDate(this.dateRangeFilter.start);
    const endDate = this.getFormatDate(this.dateRangeFilter.end);
    this.$emit('export-price-changes', { startDate, endDate });
  }

  getFormatDate(date) {
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();

    // To avoid method toISOString() convert 09/12 to 08/12 for GMT+01
    // This converts 09/12 00:00:00 GMT+01 to 08/12, so filter is left in an inconsistent state
    // This prevents the date of the data-picker from being different with the date we send to server
    return new Date(Date.UTC(year, month, day)).toISOString().substring(0, 10);
  }

  onDatePicker() {
    this.setDateRangeFilter(this.dateRange);
  }

  onMenuSelected() {
    this.setMenuGuidFilter(this.menuGuid);
  }

  onSectionSelected() {
    this.setSectionGuidFilter(this.sectionGuid);
  }

  onSaleRateSelected() {
    this.setSaleRateFilter(this.saleRate);
  }

  async onConfirmChangeFilters() {
    this.emitFilters();
    this.changeFiltersModalVisible = false;
  }

  onCloseChangeFiltersModal() {
    const dateRangeTarget = {
      start: new Date(this.oldStartDateFilter.getTime()),
      end: new Date(this.oldEndDateFilter.getTime())
    };
    this.setDateRangeFilter(dateRangeTarget);

    const saleRateTarget = this.saleRateList.find((saleRate) => saleRate.guid === this.oldSaleRateGuidFilter);
    this.setSaleRateFilter(saleRateTarget);
    this.setMenuGuidFilter(this.oldMenuGuidFilter);
    this.setSectionGuidFilter(this.oldSectionGuidFilter);
    this.changeFiltersModalVisible = false;
  }

  checkRouteLeave() {
    return window.confirm(this.$t('routeOutConfirmation'));
  }

  @Watch('dateRangeFilter')
  onUpdateDateRangeFilter() {
    const dateRangeTarget = {
      start: new Date(this.dateRangeFilter.start.getTime()),
      end: new Date(this.dateRangeFilter.end.getTime())
    };
    this.dateRange = dateRangeTarget;
  }

  @Watch('saleRateFilter')
  onUpdateSaleRateFilter() {
    this.saleRate = this.saleRateFilter;
    this.setMenuGuidFilter(null);
  }

  @Watch('menuGuidFilter')
  onUpdateMenuFilter() {
    this.menuGuid = this.menuGuidFilter;
    this.setSectionGuidFilter('');
  }

  @Watch('sectionGuidFilter')
  onUpdateSectionFilter() {
    this.sectionGuid = this.sectionGuidFilter;
  }
}
</script>

<style>
.select-size{
  max-width: 275px
}

.data-picker-size{
  min-width: 260px
}

.skeleton-size {
  width: 80%;
}
</style>
